import type { HttpError } from "@refinedev/core";
import { Amplify } from "aws-amplify";
import type { AuthTokens } from "aws-amplify/auth";
import { fetchAuthSession, signOut } from "aws-amplify/auth";
import axios, { type AxiosError } from "axios";
import dayjs from "dayjs";
import { toSafeInteger } from "lodash-es";
import { getLoginRedirectUrl, navigateTo } from "utils/common";

const TIMEOUT = toSafeInteger(import.meta.env.VITE_NETWORK_TIMEOUT) || 5000;
export const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {},
  timeout: TIMEOUT,
});

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_USER_POOL_ID,
      userPoolClientId: import.meta.env.VITE_USER_POOL_APP_CLIENT_ID,
    },
  },
});

axiosInstance.interceptors.request.use(
  async function (config) {
    const authRace = async () => {
      let cognitoToken: AuthTokens | undefined = undefined;
      const { tokens } = await fetchAuthSession();
      if (tokens) {
        cognitoToken = tokens;
      } else if (!window.location.pathname.includes("/desk/login")) {
        signOut();
        navigateTo(getLoginRedirectUrl("/desk/login"));
        return Promise.reject(new Error("This request is blocked."));
      } else {
        signOut(); // 保险起见, 清除本地的所有token;
        return Promise.reject(new Error("This request is blocked."));
      }
      const { exp = dayjs().unix() } = tokens?.accessToken.payload || {};
      if (dayjs().unix() + 1 > exp) {
        const { tokens } = await fetchAuthSession({ forceRefresh: true });
        if (tokens) cognitoToken = tokens;
      }
      return {
        ...config,
        headers: {
          ...config.headers,
          Authorization: "Bearer " + cognitoToken?.idToken?.toString(),
        } as any,
      };
    };
    const sleep = new Promise<"timeout">((resolve) => setTimeout(resolve, TIMEOUT, "timeout"));
    const result = await Promise.race([authRace(), sleep]);
    if (result === "timeout") {
      throw new Error("timeout");
    } else {
      return result;
    }
  },
  function (error: AxiosError) {
    if (error.response) {
      const { status, statusText, data } = error.response;
      const httpError: HttpError = {
        statusCode: status,
        message: statusText,
        // @ts-ignore
        errors: data,
      };
      throw httpError;
      // return Promise.reject(new Error(msg, { cause: type }));
    } else {
      return Promise.reject(error);
    }
  }
);

axiosInstance.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error: AxiosError) {
    if (error.response) {
      const { status, statusText, data } = error.response;
      const httpError: HttpError = {
        statusCode: status,
        message: statusText,
        // @ts-ignore
        errors: data,
      };
      return Promise.reject(httpError);
      // return Promise.reject(new Error(msg, { cause: type }));
    } else {
      if (error.code === "ECONNABORTED") {
        const httpError: HttpError = {
          message: error.code,
          statusCode: 504,
        };
        return Promise.reject(httpError);
      }
      return Promise.reject(error);
    }
  }
);

export const get = axiosInstance.get;
export const post = axiosInstance.post;
